import React from 'react';
import { BarChart3, Users, FileText, Presentation, Bot, Target } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';

const ServicesSection = () => {
  const services = [
    {
      id: 0,
      icon: BarChart3,
      title: "Market Research & Business Intelligence",
      description: "Competitor analysis, consumer insights, and tailored reports that give you the strategic advantage in your market.",
      features: ["Competitor Analysis", "Consumer Insights", "Market Trends", "Custom Reports"],
      color: "from-[#5f9dff] to-[#4a8ae8]"
    },
    {
      id: 1,
      icon: Users,
      title: "Student Support & Mentorship",
      description: "Case study assistance, academic guidance, and presentation coaching to excel in your educational journey.",
      features: ["Case Study Help", "Academic Guidance", "Presentation Coaching", "Career Mentoring"],
      color: "from-[#61525a] to-[#4a3f47]"
    },
    {
      id: 2,
      icon: FileText,
      title: "Business Cases & Corporate Presentations",
      description: "Professional strategy decks, visual storytelling, and persuasive narratives for corporate excellence.",
      features: ["Strategy Decks", "Visual Storytelling", "Executive Summaries", "Board Presentations"],
      color: "from-[#ff8c19] to-[#e67c15]"
    },
    {
      id: 3,
      icon: Presentation,
      title: "Startup Pitch Decks",
      description: "Investor-ready presentations with clear data, market validation, and compelling growth potential narratives.",
      features: ["Investor Decks", "Market Validation", "Financial Modeling", "Growth Strategy"],
      color: "from-[#b4dc19] to-[#9bc216]"
    },
    {
      id: 4,
      icon: Bot,
      title: "Consulting & AI Roadmaps",
      description: "Strategic growth advisory and technology integration strategies for digital transformation success.",
      features: ["AI Strategy", "Digital Transformation", "Process Optimization", "Tech Integration"],
      color: "from-[#c8aff0] to-[#b799e6]"
    },
    {
      id: 5,
      icon: Target,
      title: "Artificial Intelligence Projects",
      description: "End-to-end AI project development, from concept to deployment, tailored to your specific business needs.",
      features: ["AI Development", "Machine Learning", "Data Analytics", "Custom Solutions"],
      color: "from-[#3dd3ee] to-[#2bc4db]"
    }
  ];

  const scrollToBooking = () => {
    document.getElementById('booking-form').scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="services" className="py-20 bg-gradient-to-br from-white via-[#f7f5f2] to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1e1919] mb-6 font-['Dbsharpgroteskvariable_Vf',_Arial,_sans-serif]">
            Our Core 
            <span className="bg-gradient-to-r from-[#5f9dff] to-[#61525a] bg-clip-text text-transparent">Services</span>
          </h2>
          <p className="text-lg text-[#736c64] max-w-3xl mx-auto leading-relaxed">
            Comprehensive solutions at the intersection of education, market research, AI analytics, and strategic mentorship.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {services.map((service, index) => {
            const IconComponent = service.icon;
            return (
              <Card 
                key={service.id} 
                className="group hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border-0 bg-white/80 backdrop-blur-sm overflow-hidden"
              >
                <CardHeader className="pb-4">
                  <div className={`inline-flex w-16 h-16 rounded-2xl bg-gradient-to-br ${service.color} items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>
                  <CardTitle className="text-xl font-bold text-[#1e1919] group-hover:text-[#61525a] transition-colors duration-300 font-['Dbsharpgroteskvariable_Vf',_Arial,_sans-serif]">
                    {service.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-[#736c64] mb-6 leading-relaxed">
                    {service.description}
                  </p>
                  <div className="space-y-2 mb-6">
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center text-sm">
                        <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${service.color} mr-3`}></div>
                        <span className="text-[#61525a] font-medium">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <Button 
                    onClick={scrollToBooking}
                    className={`w-full bg-gradient-to-r ${service.color} hover:shadow-lg text-white font-semibold transition-all duration-300 transform group-hover:scale-105`}
                  >
                    Get Started
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <div className="bg-gradient-to-r from-[#5f9dff]/10 to-[#61525a]/10 rounded-3xl p-8 backdrop-blur-sm border border-white/50">
            <h3 className="text-2xl font-bold text-[#1e1919] mb-4 font-['Dbsharpgroteskvariable_Vf',_Arial,_sans-serif]">
              Ready to Transform Your Business?
            </h3>
            <p className="text-[#736c64] mb-6 max-w-2xl mx-auto">
              Get personalized solutions tailored to your specific needs. Our expert team is ready to help you achieve your goals.
            </p>
            <Button 
              onClick={scrollToBooking}
              className="bg-[#61525a] hover:bg-[#4a3f47] text-white px-8 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 hover:shadow-xl"
            >
              Book Your Consultation
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export { ServicesSection };